import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SERVER_URL } from "../../constant";
import AlertBox from "../../component/AlertBox";
import { LinearProgress } from "@mui/material";
import { HiringReqData } from "./TASingleHiringReq";
import { useSelector } from "react-redux";
import DialogBox from "../../component/DialogBox";
import { FiEdit3 } from "react-icons/fi";
import InputField from "../../component/InputField";
import SelectField from "../../component/SelectField";
import ButtonCompo from "../../component/ButtonCompo";

function SingleHiringReqManager() {
  const isEmployee = useSelector(
    (state) => state.employee?.isEmployeeAuthenticated
  );
  const employeeDataForAccess = useSelector(
    (state) => state.employee?.employeeData
  );

  const navigate = useNavigate();

  //redirect to signin if not logged in
  useEffect(() => {
    if (isEmployee !== undefined && isEmployee === false) {
      navigate("/employee/signin");
    }
  }, [isEmployee]);

  //redirect to employee dashboard if permission is not given
  useEffect(() => {
    if (
      employeeDataForAccess !== null &&
      employeeDataForAccess?.admin?.hiringReq === false
    ) {
      navigate("/employee/dashboard");
    }
  }, [employeeDataForAccess]);

  /* ----------------------------------------------------------------------------------------------------- */
  /* ----------------------------------------Main Coding Part--------------------------------------------- */
  /* ----------------------------------------------------------------------------------------------------- */

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [reload, setReload] = useState(false);
  const { reqId } = useParams();
  const [hiringReqData, setHiringReqData] = useState(undefined);
  const [role, setRole] = useState("");
  const [position, setPosition] = useState("");
  const [yoe, setYoe] = useState("");
  const [skills, setSkills] = useState("");
  const [eligibility, setEligibility] = useState("");
  const [jobDesc, setJobDesc] = useState("");
  const [joiningDuration, setJoiningDuration] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  // fetch single hiring request
  async function getHiringReq() {
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/hiring/request/get/single/request/${reqId}`,
        method: "get",
        withCredentials: true,
      });
      console.log(res.data?.data);
      setHiringReqData(res.data?.data);
    } catch (error) {
      console.log(error);
      setOpen(true);
      setSuccess(false);
      setMessage("Hiring request fetch failed");
    } finally {
      setLoading(false);
    }
  }

  async function updateHiringReq() {
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/hiring/request/edit`,
        method: "put",
        data: {
          role,
          position,
          yearsOfExperience: yoe,
          skills,
          eligibility,
          jobDescription: jobDesc,
          expectedJoiningDuration: joiningDuration,
          reqId,
        },
        withCredentials: true,
      });
      console.log(res.data);
      setReload((prev) => !prev);
      setOpen(true);
      setSuccess(true);
      setMessage("Hiring request updated successfully");
    } catch (error) {
      console.log(error);
      setOpen(true);
      setSuccess(false);
      setMessage("Hiring request update failed");
    } finally {
      setLoading(false);
    }
  }

  async function updateHiringStatus() {
    try {
      setLoading(true);
      const res = await axios({
        url: `${SERVER_URL}/api/v1/admin/hiring/request/edit`,
        method: "put",
        data: {
          status: selectedStatus,
          reqId,
        },
        withCredentials: true,
      });
      console.log(res.data);
      setReload((prev) => !prev);
      setOpen(true);
      setSuccess(true);
      setMessage("Status updated successfully");
    } catch (error) {
      console.log(error);
      setOpen(true);
      setSuccess(false);
      setMessage("Status update failed");
    } finally {
      setLoading(false);
    }
  }

  const statusArray = [
    {
      title: "Open",
      value: "Open",
      color: "primary",
    },
    {
      title: "Hold",
      value: "Hold",
      color: "warning",
    },
    {
      title: "Completed",
      value: "Completed",
      color: "success",
    },
    {
      title: "Close",
      value: "Rejected",
      color: "error",
    },
  ];

  const joiningDurationArray = [
    {
      title: "15 Days",
      value: "15 Days",
    },
    {
      title: "30 Days",
      value: "30 Days",
    },
    {
      title: "60 Days",
      value: "60 Days",
    },
    {
      title: "90 Days",
      value: "90 Days",
    },
  ];

  const setUpdateData = () => {
    setRole(hiringReqData?.role);
    setPosition(hiringReqData?.position);
    setYoe(hiringReqData?.yearsOfExperience);
    setSkills(hiringReqData?.skills);
    setEligibility(hiringReqData?.eligibility);
    setJobDesc(hiringReqData?.jobDescription);
    setJoiningDuration(hiringReqData?.expectedJoiningDuration);
  };

  useEffect(() => {
    getHiringReq();
  }, [reload]);

  return (
    <>
      {loading && (
        <LinearProgress className="!fixed !top-0 !left-0 !right-0 !h-[8px]" />
      )}
      <div className="w-full flex justify-center items-center">
        {success && (
          <AlertBox
            open={open}
            setOpen={setOpen}
            success={success}
            title={message}
          />
        )}
        {!success && (
          <AlertBox
            open={open}
            setOpen={setOpen}
            success={success}
            title={message}
          />
        )}
        <div className="w-[90vw] py-5">
          <section className="flex justify-between items-center">
            <h1 className="text-[30px] font-medium text-left w-full">
              Hiring Request Details
            </h1>
            <section className="flex gap-5 items-center">
              <DialogBox
                trigger={
                  <section
                    onClick={setUpdateData}
                    className="flex items-center border w-[10rem] justify-center gap-2 p-2 rounded-full text-slate-800 cursor-pointer hover:bg-slate-50"
                  >
                    <FiEdit3 className="text-[18px]" />
                    Edit Request
                  </section>
                }
                title="Edit Hiring Request"
                maxWidth="sm"
                actionButton={
                  <ButtonCompo onClick={updateHiringReq}>Update</ButtonCompo>
                }
              >
                <div className="space-y-5">
                  <InputField
                    label="Role"
                    multiline
                    value={role}
                    onChange={(eve) => setRole(eve?.target?.value)}
                  />
                  <InputField
                    label="Position"
                    multiline
                    value={position}
                    onChange={(eve) => setPosition(eve?.target?.value)}
                  />
                  <InputField
                    label="Experience"
                    multiline
                    value={yoe}
                    onChange={(eve) => setYoe(eve?.target?.value)}
                  />
                  <InputField
                    label="Skills"
                    multiline
                    value={skills}
                    onChange={(eve) => setSkills(eve?.target?.value)}
                  />
                  <InputField
                    label="Eligibility"
                    multiline
                    value={eligibility}
                    onChange={(eve) => setEligibility(eve?.target?.value)}
                  />
                  <InputField
                    label="Job Description"
                    multiline
                    value={jobDesc}
                    onChange={(eve) => setJobDesc(eve?.target?.value)}
                  />
                  <SelectField
                    label="Joining Duration"
                    value={joiningDuration}
                    setValue={(eve) => setJoiningDuration(eve?.target?.value)}
                    valueArray={joiningDurationArray}
                  />
                </div>
              </DialogBox>
              <DialogBox
                trigger={
                  <section
                    onClick={() => setSelectedStatus(hiringReqData?.status)}
                    className="flex items-center border w-[9rem] justify-center gap-2 p-2 rounded-full text-slate-800 cursor-pointer hover:bg-slate-50"
                  >
                    <FiEdit3 className="text-[18px]" />
                    Edit Status
                  </section>
                }
                title="Edit Hiring Request"
                actionButton={
                  <ButtonCompo onClick={updateHiringStatus}>Update</ButtonCompo>
                }
              >
                <div className="flex justify-center items-center gap-3">
                  {statusArray.map((item, index) => (
                    <ButtonCompo
                      color={item.color}
                      variant={
                        selectedStatus === item.value ? "contained" : "outlined"
                      }
                      onClick={() => setSelectedStatus(item.value)}
                    >
                      {item.title}
                    </ButtonCompo>
                  ))}
                </div>
              </DialogBox>
            </section>
          </section>
          <div className="mt-5">
            <HiringReqData hiringReqData={hiringReqData} />
          </div>
        </div>
      </div>
    </>
  );
}

export default SingleHiringReqManager;
